import type { Area } from 'react-easy-crop';

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

export function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(
  width: number,
  height: number,
  rotation: number,
): { width: number; height: number } {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
): Promise<Blob | null> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);
  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d');

  if (!croppedCtx) {
    return null;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  // As Base64 string
  // return croppedCanvas.toDataURL('image/jpeg');

  // As a blob
  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob((file) => {
      if (file) {
        resolve(file);
      } else {
        reject('Unable to get cropped image');
      }
    }, 'image/png');
  });
}

interface ResolutionValidation {
  file: File;
  width?: number;
  height?: number;
}

export const getImageDimensions = (
  file: File,
): Promise<{ naturalWidth: number; naturalHeight: number }> => {
  const img = new Image();
  img.src = URL.createObjectURL(file);

  return new Promise((resolve) => {
    img.onload = () => {
      resolve({
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      });
    };
  });
};

export const validateResolution = async ({
  file,
  width = 1400,
  height = 642,
}: ResolutionValidation): Promise<{
  isValid: boolean;
  naturalWidth: number;
  naturalHeight: number;
}> => {
  const { naturalHeight, naturalWidth } = await getImageDimensions(file);
  const isValid = naturalWidth >= width && naturalHeight >= height;

  return { isValid, naturalHeight, naturalWidth };
};

interface SizeValidation {
  file: File;
  size?: number;
}

export const validateSize = ({ file, size = 10 * 1024 * 1024 }: SizeValidation) => {
  return file.size <= size;
};

export const calculateMaxZoom = async (
  file: File,
  constraints: {
    minHeight: number;
    minWidth: number;
  },
): Promise<number> => {
  const { naturalHeight, naturalWidth } = await getImageDimensions(file);

  const calculatedZoom = Math.min(
    naturalWidth / constraints.minWidth,
    naturalHeight / constraints.minHeight,
  );

  // Ensure maxZoom is at least 1, so the image is not scaled below its original size
  return Math.max(calculatedZoom, 1);
};
